import { Entity } from "../../Entity";
import AgencyFeeFactory from "../../Factories/AgencyFeeFactory";
import { FactoryInterface } from "../../FactoryInterface";
import { AgencyFeeInterface } from "./AgencyFeeInterface";
import { CurrencyInterface } from "../../../Currency/CurrencyInterface";
import { FPPClassInterface } from "./FPPClass/FPPClassInterface";
import AgencyFeeContainer from "../../../Entity/Entities/AgencyFee/AgencyFeeContainer";
import { UnitInterface } from "../Unit/UnitInterface";
import { PropertyInterface } from "../Property/PropertyInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import Store from "../../../Store/Store";

class AgencyFee extends Entity implements AgencyFeeInterface {

  constructor(public data) {
    super();
  }

  public getFactory(): FactoryInterface {
    return AgencyFeeFactory;
  }

  get agencyFee(): AgencyFeeInterface | undefined {
    return this.relation("app.agencyFees", this.data.agencyFeeId)[0];
  }

  get id(): number {
    return this.data.id;
  }

  get companyId(): number {
    return this.data.companyId;
  }

  get agencyId(): number {
    return this.data.agencyId;
  }

  get agency(): CompanyInterface {
    return Store.getConfigurator().getState()?.app?.companies?.find((company: CompanyInterface) => company?.isAgency);
  }

  get currency(): CurrencyInterface {
    return this.data.currency;
  }

  get objectType(): number {
    return this.data.objectType;
  }

  get objectId(): number {
    return this.data.objectId;
  }

  get fixedFee(): number {
    return this.data.fixedFee;
  }

  get finalAmount(): number {
    return this.data.finalAmount;
  }

  get percentageFee(): number {
    return this.data.percentageFee;
  }

  get repeatPeriod(): number {
    return this.data.repeatPeriod;
  }

  get assignmentServiceStatus(): number {
    return this.data.assignmentServiceStatus;
  }

  get fixedPlusPercentageMode(): FPPClassInterface {
    return this.data.fixedPlusPercentageMode;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  get vat(): number {
    return this.data.vat;
  }

  get firstDate(): Date {
    return this.data.firstDate;
  }

  get lastDate(): Date {
    return this.data.lastDate;
  }

  get chargeTypeIds(): number[] {
    return this.data.chargeTypeIds;
  }

  get chargeTypes(): any[] {
    return this.chargeTypeIds ? this.chargeTypeIds.map(id => {
      return Store.getConfigurator().getState().globalTypes?.ChargeType?.[id];
    }) : []
  }

  get statusId(): number {
    return this.data.statusId;
  }

  get status(): string {
    if (this.removed) {
      return "removed"
    }
    const status = Store.getConfigurator().getState().globalTypes?.AgencyFeeStatus?.[this.statusId];

    if (!status) {
      return;
    }
    
    return status?.label?.toLowerCase();
  }

  get unit(): UnitInterface {
    if (this.objectType === AgencyFeeContainer.pickIdFromLabel("Unit")) {
      return this.newRelation("units.byId", this.data.objectId)[0];
    }
  }

  get property(): PropertyInterface {
    if (this.objectType === AgencyFeeContainer.pickIdFromLabel("Property")) {
      return this.newRelation("properties.byId", this.data.objectId)[0];
    }
  }

  get owner(): CompanyInterface {
    if (this.objectType === AgencyFeeContainer.pickIdFromLabel("Owner")) {
      return Store.getConfigurator().getState()?.app?.companies?.find((company: CompanyInterface) => company?.id === this.data.objectId);
    }
  }

  get company(): any {
    return this.relation("app.companies", this.data.companyId)[0];
  }

  get companyName(): any {
    if (this.company?.data.displayName) {
      return this.company?.data.displayName;
    }
    return this.unit?.property?.company.displayName; 
  }

  get counterparty(): CompanyInterface {
    if (this.objectType === AgencyFeeContainer.pickIdFromLabel("Unit")) {
      return this.unit?.property?.company;
    }

    if (this.objectType === AgencyFeeContainer.pickIdFromLabel("Property")) {
      return this.property?.company;
    }

    if (this.objectType === AgencyFeeContainer.pickIdFromLabel("Owner")) {
      return this.owner;
    }

    if (this.objectType === AgencyFeeContainer.pickIdFromLabel("Agency")) {
      return this.agency;
    }
  }

  get isConcept(): boolean {
    return this.status === "concept";
  }

  get isSigned(): boolean {
    return this.status === "signed";
  }

  get isEnding(): boolean {
    return this.status === "ending";
  }

  get isEnded(): boolean {
    return this.status === "ended";
  }

  public getStatusColor(): string {
    if (this.isSigned) {
      return "#43A047";
    }

    if (this.isEnding) {
      return "#F5A623";
    }

    return "#F44336";
  }

  get isReadonly(): boolean {
    return this.removed ||
           !this.isConcept;
  }

  get date(): Date {
    return this.data.date;
  }
}

export { AgencyFee };
